import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatLocalizedDate } from '@/lib/date-utils';
import { cn } from '@/lib/utils';

export interface TableDateDisplayProps {
  value: Date | string | null;
  isEmpty?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
  tooltipContent?: React.ReactNode;
  children?: React.ReactNode;
  /** Whether the cell is editable (affects cursor) */
  isEditable?: boolean;
  /** Whether this is a parent row (affects cursor/opacity) */
  isParent?: boolean;
  /** Click handler to start editing */
  onClick?: () => void;
  /** Language for date formatting */
  language?: string;
  /** Whether this field was recently modified (for highlighting) */
  isRecentlyModified?: boolean;
}

/**
 * Date display component for table inline editing
 * Shows formatted date and handles click to edit
 */
export const TableDateDisplay: React.FC<TableDateDisplayProps> = ({
  value,
  isEmpty = false,
  hasError = false,
  errorMessage,
  className,
  tooltipContent,
  children,
  isEditable = true,
  isParent = false,
  onClick,
  language,
  isRecentlyModified = false,
}) => {
  const { t, i18n } = useTranslation();
  const displayLanguage = language ?? i18n.language;

  const displayValue = value
    ? formatLocalizedDate(value, displayLanguage)
    : null;

  const interactiveClasses = isParent
    ? 'cursor-pointer opacity-50'
    : isEditable
      ? 'cursor-pointer'
      : 'cursor-not-allowed opacity-50';

  const displayClasses = cn(
    'flex h-full w-full items-center rounded-none px-2 text-sm',
    hasError
      ? 'bg-red-50 border-l-2 border-red-500'
      : isRecentlyModified
        ? 'bg-blue-50 border-l-2 border-blue-500'
        : 'bg-transparent',
    interactiveClasses,
    className
  );

  const content = (
    <div className={displayClasses} onClick={onClick}>
      {isEmpty ? (
        <span className="text-muted-foreground">
          {errorMessage ? t(errorMessage) : '—'}
        </span>
      ) : (
        <span className="truncate">{displayValue || '—'}</span>
      )}
      {children}
    </div>
  );

  if (tooltipContent || errorMessage) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        {(errorMessage || tooltipContent) && (
          <TooltipContent>
            {errorMessage ? <p>{t(errorMessage)}</p> : tooltipContent}
          </TooltipContent>
        )}
      </Tooltip>
    );
  }

  return content;
};
